import "@/_old/config/clevertap";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { App } from "../../App";
import { DEFAULT_ROUTES, DEMO_ROUTES } from "../../_old/constants/routes";
import { useWosShell } from "@/core/hooks/useWosShell";

import { RouteAuth } from "@/_old/components/auth/route";
import { WalletRoute } from "@/billing/routes";
import { CompanyRoutes } from "@/company/routes";
import { RumTracker } from "@/core/components/RumTracker";
import { NewDashboard } from "@/dashboard";
import { MyOfficesRoutes } from "@/my-offices/routes";
import { PackagesRoutes } from "@/packages/routes";
import { ReasonsOfUseRoutes } from "@/reasons-of-use/routes";
import { TasksRoutes } from "@/tasks/routes";

/**
 * Redireciona a rota raiz ("/") para o destino correto segundo a flag
 * `wos-manager-shell`: quando ativa vai para /ia-actions-center (chat-first),
 * caso contrário mantém o comportamento legado (/painel).
 */
function RootRedirect({
  dashboard,
  actionCenter,
}: {
  dashboard: string;
  actionCenter: string;
}) {
  const isWosShell = useWosShell();
  return <Navigate to={isWosShell ? actionCenter : dashboard} replace />;
}

// Componente para redirecionamento externo
function ExternalRedirect({ url }: { url: string }) {
  useEffect(() => {
    window.open(url, "_blank");
    // Volta para a página anterior após abrir a URL externa
    window.history.back();
  }, [url]);

  return null;
}
const ActionCenterPage = lazy(() => import("@/action-center/view"));
const ContractsPage = lazy(() => import("@/contracts/view"));
const PaymentsContractPage = lazy(
  () => import("@/_old/pages/contracts/payments"),
);
const ReportsPage = lazy(() => import("@/reports/view"));
const ReviewsPage = lazy(() => import("@/_old/pages/reviews"));
const InsightsPage = lazy(() =>
  import("@/insights").then((m) => ({ default: m.NewInsights })),
);
const PaymentsPage = lazy(() => import("@/_old/pages/payments"));
const PublicDownloadPage = lazy(
  () => import("@/insights/pages/public-download"),
);
const MagicLinkPage = lazy(
  () => import("@/core/components/auth/view/magic-link.page"),
);

export function Router() {
  return (
    <BrowserRouter>
      <RumTracker />
      <Routes>
        <Route path={DEFAULT_ROUTES.INDEX} element={<App />}>
          <Route
            index
            element={
              <RootRedirect
                dashboard={DEFAULT_ROUTES.DASHBOARD}
                actionCenter={DEFAULT_ROUTES.ACTION_CENTER}
              />
            }
          />
          <Route
            path={DEFAULT_ROUTES.ACTION_CENTER}
            element={
              <RouteAuth>
                <ActionCenterPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.DASHBOARD}
            element={
              <RouteAuth>
                <NewDashboard />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.REPORTS}
            element={
              <RouteAuth permissions={["read-private-rooms"]}>
                <ReportsPage />
              </RouteAuth>
            }
          />
          <Route path={DEFAULT_ROUTES.COMPANY} element={<CompanyRoutes />} />
          <Route
            path={DEFAULT_ROUTES.REASONS_OF_USE}
            element={<ReasonsOfUseRoutes />}
          />
          <Route
            path={DEFAULT_ROUTES.PAYMENTS}
            element={
              <RouteAuth permissions={["read-invoices"]}>
                <PaymentsPage />
              </RouteAuth>
            }
          />
          <Route path={DEFAULT_ROUTES.WALLET} element={<WalletRoute />} />
          <Route
            path={DEFAULT_ROUTES.CONTRACTS}
            element={
              <RouteAuth permissions={["read-contracts"]}>
                <ContractsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.PAYMENTS}
            element={
              <RouteAuth permissions={["read-invoices"]}>
                <PaymentsContractPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.REVIEWS}
            element={
              <RouteAuth>
                <ReviewsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.BOOKINGS_REPORT}
            element={<Navigate to={DEFAULT_ROUTES.INSIGHTS} />}
          />
          <Route
            path={DEFAULT_ROUTES.INSIGHTS}
            element={
              <RouteAuth permissions={["read-spaces"]}>
                <InsightsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEFAULT_ROUTES.NEW_INSIGHTS}
            element={<Navigate to={DEFAULT_ROUTES.INSIGHTS} replace />}
          />
          <Route
            path={DEFAULT_ROUTES.MY_OFFICES}
            element={<MyOfficesRoutes />}
          />
          <Route
            path={import.meta.env.VITE_LISTING_URL}
            element={
              <ExternalRedirect url={import.meta.env.VITE_LISTING_URL} />
            }
          />
          <Route path={DEFAULT_ROUTES.PACKAGES} element={<PackagesRoutes />} />
        </Route>
        <Route path={DEMO_ROUTES.INDEX} element={<App />}>
          <Route
            index
            element={
              <RootRedirect
                dashboard={DEMO_ROUTES.DASHBOARD}
                actionCenter={DEMO_ROUTES.ACTION_CENTER}
              />
            }
          />
          <Route
            path={DEMO_ROUTES.DASHBOARD}
            element={
              <RouteAuth>
                <NewDashboard />
              </RouteAuth>
            }
          />
          <Route
            path={DEMO_ROUTES.REPORTS}
            element={
              <RouteAuth permissions={["read-private-rooms"]}>
                <ReportsPage />
              </RouteAuth>
            }
          />
          <Route path={DEMO_ROUTES.COMPANY} element={<CompanyRoutes />} />
          <Route
            path={DEMO_ROUTES.REASONS_OF_USE}
            element={<ReasonsOfUseRoutes />}
          />
          <Route
            path={DEMO_ROUTES.PAYMENTS}
            element={
              <RouteAuth permissions={["read-invoice"]}>
                <PaymentsPage />
              </RouteAuth>
            }
          />
          <Route path={DEMO_ROUTES.WALLET} element={<WalletRoute />} />
          <Route
            path={DEMO_ROUTES.CONTRACTS}
            element={
              <RouteAuth permissions={["read-contracts"]}>
                <ContractsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEMO_ROUTES.PAYMENTS}
            element={
              <RouteAuth permissions={["read-contracts"]}>
                <PaymentsContractPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEMO_ROUTES.REVIEWS}
            element={
              <RouteAuth>
                <ReviewsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEMO_ROUTES.BOOKINGS_REPORT}
            element={<Navigate to={DEMO_ROUTES.INSIGHTS} />}
          />
          <Route
            path={DEMO_ROUTES.INSIGHTS}
            element={
              <RouteAuth permissions={["read-bookings"]}>
                <InsightsPage />
              </RouteAuth>
            }
          />
          <Route
            path={DEMO_ROUTES.NEW_INSIGHTS}
            element={<Navigate to={DEMO_ROUTES.INSIGHTS} replace />}
          />
          <Route path={DEMO_ROUTES.MY_OFFICES} element={<MyOfficesRoutes />} />
          <Route path={DEMO_ROUTES.PACKAGES} element={<PackagesRoutes />} />
        </Route>
        <Route path="/tasks/*" element={<TasksRoutes />} />
        {/* Rota pública (sem login): link de download enviado por e-mail/WhatsApp. */}
        <Route
          path="/baixar-relatorio/:code"
          element={
            <Suspense fallback={null}>
              <PublicDownloadPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/link"
          element={
            <Suspense fallback={null}>
              <MagicLinkPage />
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
